import {
  ContactSupport,
  Description,
  Home,
  Logout,
  ManageAccounts,
  Payment,
  Payments,
  People,
} from "@mui/icons-material";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useSingleEffect } from "Hooks/useSingleEffect";
import NavBar from "Layout/NavBar/NavBar";
import { getCurrentUser, logout, refreshToken, verifyToken } from "Services/api/auth/auth";
import { UserData } from "Services/api/auth/interfaces";
import { Backdrop, BackdropState } from "Shared/Backdrop/Backdrop";
import { useSnackbar } from "notistack";
import { Dispatch, Fragment, PropsWithChildren, SetStateAction, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import logo from "../../../../public/logo.png";
import { ConciliationForm } from "./ConciliationForm";
import { SignatureMessage } from "./SignatureMessage";

export interface DashboardProps {
  enableOnAffiliation?: boolean;
}

export default function Dashboard({
  enableOnAffiliation = false,
  children,
}: PropsWithChildren<DashboardProps>): JSX.Element {
  const [backdropState, setBackdropState] = useState<BackdropState>({
    open: true,
    msg: "Cargando ...",
  });
  const [, setClickOccurred] = useToken();

  return (
    <Box onClick={() => setClickOccurred(true)}>
      <Main enableOnAffiliation={enableOnAffiliation} setBackdropState={setBackdropState}>
        {children}
      </Main>
      <Backdrop {...backdropState} />
    </Box>
  );
}

const initialUser: UserData = {
  pk: 0,
  username: "",
  first_name: "",
  last_name: "",
  email: "",
  full_name: "",
  is_active: false,
  last_login: "",
  role: "",
  role_id: null,
  lead_id: "",
  affiliated_institution: false,
  signature: {
    pending: false,
    isDigital: false,
    message: "",
  },
  conciliation: {
    pending: false,
    initialFees: {
      affiliationFees: 0,
      distribution: {
        values: [],
        total: ["", 0],
      },
    },
  },
};

function Main({
  enableOnAffiliation = false,
  setBackdropState,
  children,
}: PropsWithChildren<DashboardProps> & { setBackdropState: Dispatch<SetStateAction<BackdropState>> }) {
  const [user, setUser] = useState<UserData>(initialUser);
  const [mobileOpen, setMobileOpen] = useState(false);
  const onLogout = useOnLogout();
  useOnLoad(setUser, setBackdropState);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  if (user.pk === 0) return <></>;

  return (
    <Fragment>
      <SideDrawer user={user} mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
      <NavBar
        loggedIn
        rightMenu={<RightMenu name={`${user.first_name} ${user.last_name}`} onLogout={onLogout} />}
        handleDrawerToggle={handleDrawerToggle}
      >
        <Box
          sx={{
            ml: {
              sm: "250px",
            },
            mt: "80px",
            px: 3,
            mb: {
              xs: "80px",
              sm: 3,
            },
          }}
        >
          <MainContent user={user} enableOnAffiliation={enableOnAffiliation} setUser={setUser}>
            {children}
          </MainContent>
        </Box>
      </NavBar>
      <BottomNav />
    </Fragment>
  );
}

function MainContent({
  user,
  setUser,
  children,
  enableOnAffiliation = false,
}: PropsWithChildren<DashboardProps> & { user: UserData; setUser: Dispatch<SetStateAction<UserData>> }) {
  if (enableOnAffiliation) return <>{children}</>;

  if (user.conciliation.pending) return <ConciliationForm user={user} setUser={setUser} />;

  if (user.signature.pending) return <SignatureMessage user={user} />;

  return <>{children}</>;
}

function SideDrawer({
  user,
  mobileOpen,
  handleDrawerToggle,
}: {
  user: UserData;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}) {
  const onLogout = useOnLogout();

  return (
    <Box component="nav">
      <Drawer
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={(theme) => ({
          [theme.breakpoints.up("sm")]: { display: "none" },
          "& .MuiDrawer-paper": {
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "#fff",
            boxSizing: "border-box",
          },
        })}
      >
        <DrawerContent user={user} onLogout={onLogout} handleDrawerToggle={handleDrawerToggle} />
      </Drawer>
      <Drawer
        variant="persistent"
        sx={(theme) => ({
          [theme.breakpoints.down("sm")]: { display: "none" },
        })}
        elevation={0}
        open
        PaperProps={{
          sx: {
            zIndex: 0,
          },
        }}
      >
        <DrawerContent user={user} onLogout={onLogout} />
      </Drawer>
    </Box>
  );
}

function BottomNav() {
  return (
    <Paper
      sx={(theme) => ({
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        [theme.breakpoints.up("sm")]: {
          display: "none",
        },
      })}
      elevation={3}
    >
      <BottomNavigation showLabels sx={{ backgroundColor: "#547725" }}>
        <BottomNavigationAction
          label="Inicio"
          icon={<Home />}
          sx={{ color: "#fff" }}
          component={RouterLink}
          to={"/productos"}
        />
        <BottomNavigationAction
          label="Solicitudes"
          icon={<Description />}
          sx={{ color: "#fff" }}
          component={RouterLink}
          to={"/solicitudes"}
        />
        <BottomNavigationAction
          label="Perfil"
          icon={<ManageAccounts />}
          sx={{ color: "#fff" }}
          component={RouterLink}
          to={"/socio"}
        />
      </BottomNavigation>
    </Paper>
  );
}

function useOnLoad(
  setUser: Dispatch<SetStateAction<UserData>>,
  setBackdropState: Dispatch<SetStateAction<BackdropState>>
) {
  const navigate = useNavigate();
  const goToLogin = () => navigate("/acceso");

  useSingleEffect(() => void onLoad());

  async function onLoad() {
    const token = sessionStorage.getItem("token");
    if (!token) return goToLogin();

    try {
      const { status, data } = await getCurrentUser();
      if (status === "success") setUser(data);
    } catch (error) {
      console.error(error);
    } finally {
      setBackdropState({
        open: false,
      });
    }
  }
}

function useToken(): [boolean, Dispatch<SetStateAction<boolean>>] {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const goToLogin = () => navigate("/acceso");
  const [clickOccurred, setClickOccurred] = useState(false);
  const onLogout = useOnLogout();

  useSingleEffect(() => {
    // @ts-expect-error removeEventListener
    const removeInvalidTokenEvent = () => removeEventListener("invalidToken", window);
    removeInvalidTokenEvent();

    window.addEventListener(
      "invalidToken",
      () => {
        goToLogin();
        enqueueSnackbar("La sesión del usuario es inválida o ha expirado", {
          variant: "error",
          preventDuplicate: true,
        });
      },
      false
    );

    return removeInvalidTokenEvent;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const token = sessionStorage.getItem("token");
      if (!token) return void onLogout();

      const refresh = sessionStorage.getItem("refresh");
      if (!refresh) return void onLogout();

      if (clickOccurred)
        void refreshToken(refresh)
          .then(({ access }) => {
            sessionStorage.setItem("token", access);
            setClickOccurred(false);
          })
          .catch(() => onLogout());
      else {
        void verifyToken(token).catch(() => onLogout());
      }
    }, 120000);

    return () => clearInterval(interval);
  }, [clickOccurred]);

  return [clickOccurred, setClickOccurred];
}

interface RightMenuProps {
  name: string;
  onLogout: () => Promise<void>;
}

function RightMenu({ name, onLogout }: RightMenuProps) {
  const theme = useTheme();

  return (
    <Stack direction="row" alignItems="center" spacing={3}>
      <Typography
        sx={(theme) => ({
          [theme.breakpoints.down("sm")]: {
            display: "none",
          },
        })}
      >
        Bienvenido/a {name}
      </Typography>

      <IconButton className="logout-button" sx={{ backgroundColor: "#fff" }} component={RouterLink} to="/contacto">
        <ContactSupport htmlColor={theme.palette.primary.main} />
      </IconButton>

      <IconButton
        className="logout-button"
        sx={{ backgroundColor: "#fff" }}
        onClick={() => void onLogout()}
        aria-label="cerrar sesión"
        data-testid="logout"
      >
        <Logout htmlColor={theme.palette.primary.main} />
      </IconButton>
    </Stack>
  );
}

interface DrawerContentProps {
  user: UserData;
  handleDrawerToggle?: () => void;
  onLogout: () => Promise<void>;
}

function DrawerContent({ user, handleDrawerToggle = () => undefined, onLogout }: DrawerContentProps) {
  const isAffiliation = user.conciliation.pending || user.signature.pending;

  return (
    <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle}>
      <Toolbar sx={{ px: [2] }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box src={logo} sx={{ height: 40 }} component="img" alt="Cooperativa Barcelona logo" />
        </Stack>
      </Toolbar>
      <List>
        <NavItem route="/productos" text="Inicio" icon={Home} />
        <NavItem route="/solicitudes" text="Solicitudes" icon={Description} disabled={isAffiliation} />
        <NavItem route="/retiro/solicitud" text="Retiro Electrónico" icon={Payments} disabled={isAffiliation} />
        <NavItem route="" text="Pagos de servicios" icon={Payment} disabled />
        <NavItem route="" text="Beneficiarios" icon={People} disabled />
        <NavItem route="/socio" text="Perfil" icon={ManageAccounts} />
        <NavItem route="/contacto" text="Contacto" icon={ContactSupport} />

        <ListItem disablePadding>
          <ListItemButton onClick={() => void onLogout()} aria-label="cerrar sesión">
            <>
              <Logout sx={{ marginRight: "6px" }} />
              <ListItemText primary="Salir" />
            </>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
}

function NavItem({
  route,
  text,
  icon: Icon,
  disabled = false,
}: {
  route: string;
  text: string;
  icon?: React.ElementType | undefined;
  disabled?: boolean;
}) {
  return (
    <ListItem disablePadding>
      <ListItemButton component={RouterLink} to={route} disabled={disabled}>
        <>
          {Icon && <Icon sx={{ marginRight: "6px" }} />}
          <ListItemText primary={text} />
        </>
      </ListItemButton>
    </ListItem>
  );
}

function useOnLogout() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const goToLogin = () => navigate("/acceso");

  return async function onLogout() {
    try {
      await logout();
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh");
      goToLogin();
    } catch (error) {
      enqueueSnackbar("Ha ocurrido un error al intentar cerrar sesión", {
        variant: "error",
      });
      console.error(error);
    }
  };
}
